import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Crosshair, MapPin, Check, RefreshCw, ExternalLink, Map as MapIcon } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Button, Badge, Alert } from '../components/ui'
import { jarakMeter, formatJarak } from '../components/PetaJaringanPanels'

// Ambil Lokasi GPS (Peta Pelanggan fase 3, 2026-09-20) -- halaman satu tujuan utk teknisi yg SEDANG
// berada di lokasi pelanggan: buka link /ambil-lokasi/:username (disalin admin dari kartu pelanggan
// di Peta), tekan satu tombol, simpan. Wajib login (admin/teknisi) -- bukan link publik bertoken,
// krn menulis koordinat pelanggan. Pembacaan GPS pertama di HP sering meleset puluhan meter, jadi
// dipantau beberapa detik (watchPosition) & dipakai titik dgn akurasi terbaik, bukan yg pertama datang.
const AKURASI_TARGET_M = 15      // sudah sebagus ini -> langsung berhenti mencari
const BATAS_WAKTU_MS = 15000     // paling lama mencari, lalu pakai yg terbaik sejauh itu
const TONE_STATUS = { AKTIF: 'success', ISOLIR: 'warning', BERHENTI: 'danger' }
const LABEL_STATUS = { AKTIF: 'Aktif', ISOLIR: 'Isolir', BERHENTI: 'Berhenti' }

function pesanErrorGps(err) {
  if (err && err.code === 1) return 'Izin lokasi ditolak. Izinkan akses lokasi untuk situs ini di pengaturan browser, lalu coba lagi.'
  if (err && err.code === 2) return 'Lokasi tidak tersedia. Pastikan GPS/Lokasi di HP menyala.'
  return 'Sinyal GPS belum didapat. Coba di tempat terbuka.'
}

function nilaiAkurasi(m) {
  if (m <= 20) return { tone: 'success', label: 'Akurat', saran: '' }
  if (m <= 50) return { tone: 'warning', label: 'Cukup', saran: 'Kalau bisa, ambil ulang di tempat yang lebih terbuka.' }
  return { tone: 'danger', label: 'Kurang akurat', saran: 'Sinyal GPS lemah (mungkin di dalam bangunan). Pindah ke luar/dekat jendela lalu ambil ulang.' }
}

export default function AmbilLokasi() {
  const { username } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [fase, setFase] = useState('idle')        // idle | mencari | siap
  const [live, setLive] = useState(null)          // akurasi terkini selama mencari (meter)
  const [fix, setFix] = useState(null)            // { lat, lng, akurasi }
  const [gpsError, setGpsError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [tersimpan, setTersimpan] = useState(false)
  const watchRef = useRef(null)
  const timerRef = useRef(null)

  const muat = useCallback(async () => {
    try {
      const res = await api.get(`/peta-pelanggan/${encodeURIComponent(username)}`)
      setItem(res.data)
      setLoadError('')
    } catch (e) {
      setLoadError(e.response?.status === 404 ? 'Pelanggan tidak ditemukan. Cek kembali link-nya.' : (e.response?.data?.message || 'Gagal memuat data pelanggan'))
    }
  }, [username])

  useEffect(() => { muat() }, [muat])

  const hentikan = useCallback(() => {
    if (watchRef.current !== null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
  }, [])
  useEffect(() => hentikan, [hentikan]) // berhenti kalau halaman ditutup

  function cari() {
    if (!navigator.geolocation) { setGpsError('Browser ini tidak mendukung GPS.'); return }
    hentikan()
    setGpsError(''); setSaveError(''); setTersimpan(false); setFix(null); setLive(null); setFase('mencari')
    let terbaik = null
    const selesai = () => {
      hentikan()
      if (terbaik) { setFix(terbaik); setFase('siap') } else { setFase('idle'); setGpsError(pesanErrorGps()) }
    }
    watchRef.current = navigator.geolocation.watchPosition(
      pos => {
        const c = pos.coords
        const cur = { lat: c.latitude, lng: c.longitude, akurasi: c.accuracy }
        if (!terbaik || cur.akurasi < terbaik.akurasi) terbaik = cur
        setLive(Math.round(cur.akurasi))
        if (cur.akurasi <= AKURASI_TARGET_M) selesai()
      },
      err => {
        if (err.code === 3 && terbaik) return selesai() // timeout tapi sudah punya titik: pakai itu
        hentikan(); setFase('idle'); setGpsError(pesanErrorGps(err))
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: BATAS_WAKTU_MS }
    )
    timerRef.current = setTimeout(selesai, BATAS_WAKTU_MS)
  }

  async function simpan() {
    if (!fix) return
    setSaving(true); setSaveError('')
    try {
      await api.put(`/peta-pelanggan/${encodeURIComponent(username)}/lokasi`, {
        latitude: fix.lat, longitude: fix.lng, sumber: 'gps', akurasi_m: Math.round(fix.akurasi)
      })
      setTersimpan(true)
      await muat()
    } catch (e) {
      setSaveError(e.response?.data?.message || 'Gagal menyimpan lokasi')
    } finally { setSaving(false) }
  }

  if (loadError) {
    return (
      <div>
        <PageHeader title="Ambil Lokasi GPS" />
        <Alert type="error">{loadError}</Alert>
      </div>
    )
  }
  if (!item) return <div><PageHeader title="Ambil Lokasi GPS" /><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Memuat...</div></div>

  const adaLokasiLama = item.lat !== null
  const jarakLama = fix && adaLokasiLama ? jarakMeter({ lat: item.lat, lng: item.lng }, fix) : null
  const nilai = fix ? nilaiAkurasi(fix.akurasi) : null

  return (
    <div style={{ maxWidth: 520 }}>
      <PageHeader title="Ambil Lokasi GPS" subtitle="Berdiri di titik rumah/bangunan pelanggan, lalu ambil lokasi." />

      <Card style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{item.nama || item.ppp_username}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.ppp_username}</div>
          </div>
          <Badge tone={TONE_STATUS[item.status] || 'neutral'}>{LABEL_STATUS[item.status] || item.status}</Badge>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
          <div>{item.alamat || 'Alamat belum diisi'}</div>
          <div style={{ marginTop: 4 }}>
            {adaLokasiLama
              ? <>Lokasi tersimpan: {item.lat.toFixed(6)}, {item.lng.toFixed(6)}{' '}
                  <a href={`https://www.google.com/maps?q=${item.lat},${item.lng}`} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>lihat</a></>
              : 'Belum ada lokasi tersimpan.'}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 16 }}>
        {gpsError && <Alert type="error">{gpsError}</Alert>}
        {saveError && <Alert type="error">{saveError}</Alert>}
        {tersimpan && <Alert type="success">Lokasi tersimpan. Terima kasih!</Alert>}

        {fase === 'idle' && !tersimpan && (
          <Button icon={Crosshair} onClick={cari} style={{ width: '100%', padding: '14px 16px', fontSize: 15 }}>
            {adaLokasiLama ? 'Ambil Ulang Lokasi Saya' : 'Ambil Lokasi Saya'}
          </Button>
        )}

        {fase === 'mencari' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Mencari sinyal GPS…</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 12px' }}>
              {live === null ? 'Tunggu sebentar, jangan tutup halaman ini.' : `Akurasi saat ini ±${live} m (target ±${AKURASI_TARGET_M} m)`}
            </div>
            <Button variant="soft" onClick={() => { hentikan(); setFase('idle') }}>Batal</Button>
          </div>
        )}

        {fase === 'siap' && fix && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <MapPin size={18} color="var(--brand)" />
              <span style={{ fontWeight: 700, fontSize: 15 }}>{fix.lat.toFixed(6)}, {fix.lng.toFixed(6)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Badge tone={nilai.tone}>{nilai.label} · ±{Math.round(fix.akurasi)} m</Badge>
              <a href={`https://www.google.com/maps?q=${fix.lat},${fix.lng}`} target="_blank" rel="noreferrer"
                style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Cek di Google Maps <ExternalLink size={12} />
              </a>
            </div>
            {nilai.saran && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 8 }}>{nilai.saran}</div>}
            {jarakLama !== null && !tersimpan && (
              <div style={{ fontSize: 12.5, marginBottom: 10, color: jarakLama > 200 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: jarakLama > 200 ? 600 : 400 }}>
                Titik ini berjarak {formatJarak(jarakLama)} dari lokasi yang tersimpan{jarakLama > 200 ? ' — pastikan Anda memang di rumah pelanggan yang benar.' : '.'}
              </div>
            )}
            {!tersimpan && (
              <div style={{ display: 'grid', gap: 8 }}>
                <Button icon={Check} loading={saving} onClick={simpan} style={{ width: '100%', padding: '13px 16px', fontSize: 15 }}>
                  Simpan Lokasi Ini
                </Button>
                <Button variant="soft" icon={RefreshCw} disabled={saving} onClick={cari} style={{ width: '100%' }}>Ambil ulang</Button>
              </div>
            )}
          </div>
        )}

        {tersimpan && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <Button icon={MapIcon} onClick={() => navigate(`/peta-pelanggan?fokus=${encodeURIComponent(username)}`)}>Lihat di Peta</Button>
            <Button variant="soft" icon={RefreshCw} onClick={() => { setTersimpan(false); setFase('idle'); setFix(null) }}>Ambil ulang</Button>
          </div>
        )}
      </Card>
    </div>
  )
}
